import { openDB, IDBPDatabase } from 'idb';

export interface SentenceItem {
  id: string;
  tag: string;
  text: string;
  html?: string;
  start: number;
  end: number;
  duration: number;
  /** v3: título exibido acima do parágrafo (capítulo/seção). */
  heading?: string;
  /** v3: frases do parágrafo, na ordem do texto. */
  phraseTexts?: string[];
  /** v3: tempo de cada frase (n = índice em phraseTexts; s/e em segundos globais). */
  phrases?: { n: number; s: number; e: number }[];
}

export interface SyncTrack {
  start: number;
  duration: number;
  url: string;
}

// Ajuste manual de sincronia por livro (o registro do IndexedDB não existe para livros do R2)
const syncOffsetKey = (bookId: string) => `libritui_sync_offset_${bookId}`;
export function loadSavedSyncOffset(bookId: string): number {
  try {
    const v = parseFloat(localStorage.getItem(syncOffsetKey(bookId)) || '0');
    return Number.isFinite(v) ? v : 0;
  } catch {
    return 0;
  }
}
export function saveSyncOffset(bookId: string, offset: number): void {
  try {
    if (offset === 0) localStorage.removeItem(syncOffsetKey(bookId));
    else localStorage.setItem(syncOffsetKey(bookId), String(offset));
  } catch {}
}

export interface SyncedJsonData {
  book_id: string;
  title: string;
  author: string;
  language: string;
  accuracy_score?: number;
  audio_file?: string;
  total_duration_seconds?: number;
  total_sentences?: number;
  sentences: SentenceItem[];
}

export interface SavedSyncAudiobook {
  id: string;
  book_id: string;
  slug?: string;
  folder?: string;
  sessionToken?: string;
  currentParagraphId?: string;
  isFavorited?: boolean;
  title: string;
  author: string;
  language: string;
  accuracy_score?: number;
  audio_file_name?: string;
  total_duration_seconds?: number;
  total_sentences?: number;
  sentences: SentenceItem[];
  /** v3: vários MP3 numa linha do tempo única (start = segundo global em que a faixa começa). */
  tracks?: SyncTrack[];
  audioBlob?: Blob;
  audioSrc?: string;
  addedAt: number;
  savedTime?: number;
  syncOffset?: number;
}

export type SavedSyncAudiobookMeta = Omit<SavedSyncAudiobook, 'audioBlob' | 'sentences'>;

const DB_NAME = 'libritui_sync_audiobooks_db';
const STORE_NAME = 'sync_audiobooks';
const DB_VERSION = 1;

class SyncAudiobookStorageService {
  private dbPromise: Promise<IDBPDatabase<any>> | null = null;

  constructor() {
    if (typeof window !== 'undefined') {
      this.initDb();
    }
  }

  private initDb() {
    this.dbPromise = openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('by_addedAt', 'addedAt');
        }
      },
    });
  }

  async getAllAudiobooks(): Promise<SavedSyncAudiobookMeta[]> {
    if (!this.dbPromise) return [];
    try {
      const db = await this.dbPromise;
      const tx = db.transaction(STORE_NAME, 'readonly');
      const store = tx.store;
      const all = await store.getAll();
      return all.map(({ audioBlob, sentences, ...rest }) => rest).sort((a, b) => b.addedAt - a.addedAt);
    } catch (err) {
      return [];
    }
  }

  async getAudiobook(id: string): Promise<SavedSyncAudiobook | null> {
    if (!this.dbPromise) return null;
    try {
      const db = await this.dbPromise;
      const record = await db.get(STORE_NAME, id);
      return record || null;
    } catch (err) {
      return null;
    }
  }

  async saveAudiobook(jsonData: SyncedJsonData, audioBlob: Blob): Promise<SavedSyncAudiobook> {
    if (!this.dbPromise) throw new Error('IndexedDB unavailable');
    const db = await this.dbPromise;
    const id = jsonData.book_id ? `sync_${jsonData.book_id}` : `sync_${Date.now()}`;
    const record: SavedSyncAudiobook = {
      id,
      book_id: jsonData.book_id || id,
      title: jsonData.title || 'Audiobook',
      author: jsonData.author || 'Desconhecido',
      language: jsonData.language || 'en',
      accuracy_score: jsonData.accuracy_score,
      audio_file_name: jsonData.audio_file,
      total_duration_seconds: jsonData.total_duration_seconds,
      total_sentences: jsonData.total_sentences || (jsonData.sentences ? jsonData.sentences.length : 0),
      sentences: jsonData.sentences || [],
      audioBlob,
      addedAt: Date.now(),
      savedTime: 0,
      syncOffset: 0,
    };
    try {
      await db.put(STORE_NAME, record);
    } catch (err) {
      console.warn('[SyncAudiobookStorage] Storing audioBlob in IndexedDB failed (file may be very large). Storing metadata only:', err);
      const recordMetaOnly = { ...record, audioBlob: undefined };
      await db.put(STORE_NAME, recordMetaOnly).catch(() => {});
    }
    return record;
  }

  async updateProgress(id: string, savedTime: number): Promise<void> {
    if (!this.dbPromise) return;
    try {
      const db = await this.dbPromise;
      const record = await db.get(STORE_NAME, id);
      if (record) {
        record.savedTime = savedTime;
        await db.put(STORE_NAME, record);
      }
    } catch (err) {}
  }

  async updateOffset(id: string, syncOffset: number): Promise<void> {
    if (!this.dbPromise) return;
    try {
      const db = await this.dbPromise;
      const record = await db.get(STORE_NAME, id);
      if (record) {
        record.syncOffset = syncOffset;
        await db.put(STORE_NAME, record);
      }
    } catch (err) {}
  }

  async deleteAudiobook(id: string): Promise<void> {
    if (!this.dbPromise) return;
    try {
      const db = await this.dbPromise;
      await db.delete(STORE_NAME, id);
    } catch (err) {}
  }
}

export const syncAudiobookStorage = new SyncAudiobookStorageService();
